import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Benefit from '../../../shared/benefit/benefit';
import AnalyticsManager from '../../../../app/analytics';
import { EmarsysManager } from '../../../../app/emarsys';
import {
  cleanUpObject,
  combineClassNames,
  createFiltersUrl,
} from '../../../../app/functions';
import searchIcon from '../../../../assets/icons/search-icon-white.svg';
import closeIcon from '../../../../assets/icons/x-close-mobile-icon.svg';
import lastSearchIcon from '../../../../assets/icons/last-search-icon.svg';
import fireIcon from '../../../../assets/icons/fire.svg';
import spinLoaderIcon from '../../../../assets/icons/spin-loader.svg';
import * as routes from '../../../../constants/routes';
import {
  AUTOCOMPLETE_STATUS,
  cleanUserSearchHistory,
  setHomepageSearchQuery,
  setSearchFilters,
} from '../../../../redux/actions';
import { PREVIEW_STATUS, SEARCH_DISPLAY_STATUS, useSearchAutocomplete } from './useSearchAutocomplete';
import {
  getMatchingCategories,
  getMatchingSubCategories,
  highlightMatch,
  limitCategoryResults,
} from './utils/searchMatchers';
import './index.scss';

const Search = () => {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();

  const userData = useSelector((store) => store.config.UserData);
  const texts = useSelector((store) => store.config.Translations);
  const filters = useSelector((store) => store.state.benefitSearchFilters);
  const categoriesArr = useSelector((store) => cleanUpObject(store.config.categoriesArr));
  const hotSearches = useSelector((store) => store.config.HotSearches);
  const isMobile = (useSelector((store) => store.state.deviceState)) === 'mobile';

  const [hidden, setHidden] = useState(false);

  const {
    inputRef,
    resultRef,
    isFocused,
    setIsFocused,
    searchQuery,
    typedQuery,
    selectedIndex,
    results,
    autocompleteStatus,
    previewBenefits,
    previewStatus,
    searchDisplayStatus,
    getMatchQuery,
    handleInput,
    handleKeyDown,
    onFocus,
    performSearch,
    resetSearchState,
    handleClearTextClick,
    closePreviewBenefit,
    blurInput,
    unfocus,
  } = useSearchAutocomplete();

  const matchQuery = getMatchQuery();

  const categoryMatches = useMemo(() => {
    if (!matchQuery) return { categories: [], subCategories: [] };

    const categories = getMatchingCategories(categoriesArr.Options, matchQuery);
    const categoryTexts = categories.map((item) => item.text);
    const subCategories = getMatchingSubCategories(
      categoriesArr.SubCategories,
      matchQuery,
      categoryTexts,
    );

    return limitCategoryResults(categories, subCategories);
  }, [categoriesArr, matchQuery]);

  const onHotSearchClick = (key) => {
    setIsFocused(false);
    AnalyticsManager.logHotSearch(hotSearches[key]);
    dispatch(setHomepageSearchQuery(hotSearches[key]));
  };

  const getHotSearches = () => {
    let res = [];
    for (let key in hotSearches) {
      res.push(
        <div key={key} onClick={onHotSearchClick.bind(this, key)}>
          <NavLink to={`${routes.search_results}/${hotSearches[key]}`} className="hot-search-wrapper">
            <img src={fireIcon} alt="Fire Icon"/>
            <span>{hotSearches[key]}</span>
          </NavLink>
        </div>,
      )
    }
    return res;
  };

  const handleCategoryClick = (id, text, key, cat_id = '') => {
    let newFilters = {};
    if (key === 'subCategory') {
      const data = categoriesArr.Options.filter(item => {
        return parseInt(item.id) === parseInt(cat_id);
      });
      newFilters = {
        ...filters,
        [key]: {
          ...filters[key],
          id: id,
          text: text,
          active: false,
        },
        category: {
          ...filters.category,
          id: data[0].id,
          text: data[0].text,
          active: false,
        },
      };
    } else {
      newFilters = {
        ...filters,
        [key]: {
          ...filters[key],
          id: id,
          text: text,
          active: false,
        },
      };
    }
    const urlParams = createFiltersUrl(newFilters);
    dispatch(setSearchFilters(newFilters))
    history.push(`${routes.benefits}?${urlParams}`);
    unfocus();
    blurInput();
  };

  const getCategoriesJSX = () => {
    const { categories, subCategories } = categoryMatches;
    const items = [
      ...categories.map((item, idx) => (
        <div
          key={`cat-${idx}`}
          className="category-wrapper"
          onClick={() => handleCategoryClick(item.id, item.text, 'category')}
        >
          {item.text}
          <span>{` (${item.count})`}</span>
        </div>
      )),
      ...subCategories.map(({ item, parentKey }, idx) => (
        <div
          key={`sub-${idx}-${parentKey}`}
          onClick={() => handleCategoryClick(item.id, item.text, 'subCategory', parentKey)}
          className="category-wrapper"
        >
          {item.text}
          <span>{` (${item.count})`}</span>
        </div>
      )),
    ];

    return items;
  };

  const getLastSearches = () => {
    let res = [];
    if (userData) {
      userData.lastSearches.forEach((item, idx) => {
        res.push(
          <div key={idx} onClick={resetSearchState}>
            <NavLink
              to={`${routes.search_results}/${item}`}
              className="last-search-wrapper"
            >
              <img src={lastSearchIcon} alt="Last Search Icon"/>
              <span>{item}</span>
            </NavLink>
          </div>,
        )
      })
    }
    return res;
  };

  const getBenefits = () => previewBenefits.map((item) => (
    <div key={item.id} onClick={closePreviewBenefit} className="option">
      <Benefit
        data={item}
        className="search-option"
        smallVersion
      />
    </div>
  ));

  const getSuggestions = () => {
    if (autocompleteStatus !== AUTOCOMPLETE_STATUS.RESULTS || !results.length) return [];

    return results.map((item, index) => (
      <div
        key={item}
        className={combineClassNames(
          'suggestion-wrapper',
          index === selectedIndex && 'selected active',
        )}
        onClick={() => performSearch(item)}
        role="option"
        aria-selected={index === selectedIndex}
      >
        {highlightMatch(item, matchQuery)}
      </div>
    ));
  };

  const handleSubmitClick = () => {
    const query = selectedIndex >= 0 && results[selectedIndex]
      ? results[selectedIndex]
      : typedQuery;
    if (query !== '') {
      EmarsysManager.emarsysSearchRecommendation(query);
      performSearch(query);
    }
  };

  const handleCleanOnClick = () => {
    dispatch(cleanUserSearchHistory());
  };

  useEffect(() => {
    setHidden(location.pathname === routes.legal_advice);
  }, [location.pathname]);

  const categoriesJSX = getCategoriesJSX();
  const suggestionsJSX = getSuggestions();
  const benefitsJSX = getBenefits();
  const lastSearchesJSX = getLastSearches();
  const hotSearchesJSX = getHotSearches();
  const focusClassName = isFocused && isMobile ? 'focus' : '';

  return (
    <div
      className={combineClassNames(
        'search-window-wrapper', 'active',
        hidden && 'hide-search',
      )}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="search-window">
        <div className={`search-top ${focusClassName}`}>
          <label className="input-label">
            <input
              type="text"
              aria-label={texts['header_search_placeholder']}
              placeholder={texts['header_search_placeholder']}
              className="search-box"
              onFocus={onFocus}
              autoComplete="off"
              name="autocomplete"
              value={searchQuery}
              onChange={handleInput}
              onKeyDown={handleKeyDown}
              ref={inputRef}
            />
            {searchQuery !== '' &&
              <img src={closeIcon} alt="Close Icon" className="clear-input" onClick={handleClearTextClick}/>}
          </label>
          <div className="submit" onClick={handleSubmitClick}>
            <img src={searchIcon} alt="Search Icon" className="search-icon"/>
          </div>
        </div>
        <div className={`results ${isFocused ? 'focus' : ''}`} ref={resultRef}>
          {searchQuery !== '' && (
            <div className="search-results">
              {searchDisplayStatus === SEARCH_DISPLAY_STATUS.MINIMUM_LENGTH && (
                <p className="search-status-message">
                  {texts['search_minimum_length_message']}
                </p>
              )}

              {searchDisplayStatus === SEARCH_DISPLAY_STATUS.LOADING && (
                <p className="search-status-message">
                  <img src={spinLoaderIcon} alt="Loading..." className="loading-icon"/>
                  {texts['search_loading_message']}
                </p>
              )}

              {searchDisplayStatus === SEARCH_DISPLAY_STATUS.NO_RESULTS && (
                <p className="search-status-message">
                  {texts['search_no_results_message']}
                </p>
              )}

              {suggestionsJSX.length > 0 && (
                <>
                  <div className="mini-title">
                    <span>{texts['search_autocomplete']}</span>
                    <div className="underline"/>
                  </div>
                  <div className="match-container" role="listbox">
                    {suggestionsJSX}
                  </div>
                </>
              )}

              {categoriesJSX.length > 0 && (
                <>
                  <div className="mini-title">
                    <span>{texts['search_categories']}</span>
                    <div className="underline"/>
                  </div>
                  <div className="match-categories">
                    {categoriesJSX}
                  </div>
                </>
              )}

              {((previewStatus === PREVIEW_STATUS.LOADING && suggestionsJSX.length > 0) || benefitsJSX.length > 0) && (
                <>
                  <div className="mini-title">
                    <span>{texts['search_benefits']}</span>
                    <div className="underline"/>
                  </div>
                  {previewStatus === PREVIEW_STATUS.LOADING && (
                    <p className="search-status-message">
                      <img src={spinLoaderIcon} alt="Loading..." className="loading-icon"/>
                      {texts['search_loading_message']}
                    </p>
                  )}
                  {benefitsJSX.length > 0 && (
                    <div className="benefits-container">
                      {benefitsJSX}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {searchQuery === '' && (
            <>
              {lastSearchesJSX.length > 0 && (
                <>
                  <div className="mini-title">
                    <span>{texts['search_last_searches']}</span>
                    <div className="underline"/>
                    <span className="clean" onClick={handleCleanOnClick}>{texts['search_last_clean']}</span>
                  </div>
                  <div className="last-searches-container">
                    {lastSearchesJSX}
                  </div>
                </>
              )}
              <div className="mini-title">
                <span>{texts['search_hot_searches']}</span>
                <div className="underline"/>
              </div>
              <div className="hot-searches-container">
                {hotSearchesJSX}
              </div>
            </>
          )}
        </div>
      </div>
      {isMobile && <div className={`backdrop ${focusClassName}`} onClick={() => setIsFocused(false)}/>}
    </div>);
};

export default Search;
